import React, { Component } from 'react';
import { Box, Button, Card, Flex, Heading, Text } from 'core/components';
import styled from 'styled-components';
import Table from 'core/components/HTMLTable';

import { observer } from 'mobx-react';
import { FiTarget, FiServer } from 'react-icons/fi';

const StatsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin: 8px 0;
`;

const StatItem = styled(Box)`
  text-align: center;

  .stat-label {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.muted};
    text-transform: uppercase;
    font-weight: 600;
  }

  .stat-value {
    font-size: 16px;
    font-weight: bold;
  }
`;

const ResultSection = styled(Card)`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .section-header {
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-content {
    padding: 12px;
  }
`;

const HighlightedStatValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding: 2px 4px;
  border-radius: 16px;

  &.max {
    background-color: ${({ theme }) => theme.colors.calloutOutlineBackgrounds.danger};
    color: ${({ theme }) => theme.colors.danger};
  }

  &.min {
    background-color: ${({ theme }) => theme.colors.calloutOutlineBackgrounds.success};
    color: ${({ theme }) => theme.colors.success};
  }
`;

const HighlightedTableRow = styled.tr`
  &.max {
    background-color: ${({ theme }) => theme.colors.calloutOutlineBackgrounds.danger};
    color: ${({ theme }) => theme.colors.danger};
    font-weight: bold;
  }

  &.min {
    background-color: ${({ theme }) => theme.colors.calloutOutlineBackgrounds.success};
    color: ${({ theme }) => theme.colors.success};
    font-weight: bold;
  }
`;

const HostnameDiv = styled.div`
  font-size: 11px;
  color: #666;

  .max & {
    color: ${({ theme }) => theme.colors.danger};
  }

  .min & {
    color: ${({ theme }) => theme.colors.success};
  }
`;

@observer
class LiveSynthToolResult extends Component {
  formatRtt(rtt) {
    return `${rtt.toFixed(3)} ms`;
  }

  formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleTimeString();
  }

  renderPingStatistics(statistics) {
    if (!statistics) {
      return null;
    }

    return (
      <StatsGrid>
        <StatItem>
          <div className="stat-label">Avg RTT</div>
          <div className="stat-value">{this.formatRtt(statistics.avg)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Min RTT</div>
          <HighlightedStatValue className="min">{this.formatRtt(statistics.min)}</HighlightedStatValue>
        </StatItem>
        <StatItem>
          <div className="stat-label">Max RTT</div>
          <HighlightedStatValue className="max">{this.formatRtt(statistics.max)}</HighlightedStatValue>
        </StatItem>
        <StatItem>
          <div className="stat-label">Packet Loss</div>
          <div className="stat-value">{statistics.loss}%</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Std Dev</div>
          <div className="stat-value">{statistics.stddev.toFixed(3)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Packets Sent</div>
          <div className="stat-value">{statistics.replies || 'N/A'}</div>
        </StatItem>
      </StatsGrid>
    );
  }

  renderPingResponses(responses) {
    if (!responses || responses.length === 0) {
      return null;
    }

    // Find the highest and lowest RTT values
    const maxRtt = Math.max(...responses.map((r) => r.rtt));
    const minRtt = Math.min(...responses.map((r) => r.rtt));

    return (
      <Table interactive bordered>
        <thead>
          <tr>
            <th>Seq</th>
            <th>RTT</th>
            <th>From</th>
            <th>TX Time</th>
            <th>RX Time</th>
            <th>TTL</th>
            {/* <th>Size</th> */}
          </tr>
        </thead>
        <tbody>
          {responses.map((response) => {
            const isHighestRtt = response.rtt === maxRtt;
            const isLowestRtt = response.rtt === minRtt && maxRtt !== minRtt;

            let className = '';
            if (isHighestRtt) {
              className = 'max';
            } else if (isLowestRtt) {
              className = 'min';
            }

            return (
              <HighlightedTableRow key={`response-${response.seq}`} className={className}>
                <td>{response.seq}</td>
                <td>{this.formatRtt(response.rtt)}</td>
                <td>{response.from}</td>
                <td>{this.formatTimestamp(response.tx)}</td>
                <td>{this.formatTimestamp(response.rx)}</td>
                <td>{response.replyTtl}ms</td>
                {/* <td>{response.replySize}B</td> */}
              </HighlightedTableRow>
            );
          })}
        </tbody>
      </Table>
    );
  }

  renderPingResult(pingData, target, agentId, agent_name, testId) {
    if (!pingData || !pingData.ping) {
      return null;
    }

    const { ping } = pingData;
    const { statistics, responses } = ping;

    return (
      <ResultSection key={`${target}-${agentId}`}>
        <div className="section-header">
          <FiTarget />
          <Text fontWeight="medium">
            Ping Results: {ping.src} → {target}
          </Text>
          <Box ml="auto" fontSize="12px" color="muted">
            Agent: {agent_name || agentId}
          </Box>
        </div>
        <Flex flexDirection="column" gap={2}>
          <Text color="muted" small>
            {ping.method} | {ping.pingSent} probes | {ping.timeout}s timeout | {ping.probeSize}B probe size | id:
            {testId}
          </Text>

          {statistics && this.renderPingStatistics(statistics)}

          {responses && responses.length > 0 && this.renderPingResponses(responses)}
        </Flex>
      </ResultSection>
    );
  }

  renderTraceStatistics(trace) {
    if (!trace || !trace.hops || trace.hops.length === 0) {
      return null;
    }

    const rtts = trace.hops.map((hop) => hop.rtt).filter((rtt) => rtt > 0);
    const avgRtt = rtts.length > 0 ? rtts.reduce((sum, rtt) => sum + rtt, 0) / rtts.length : 0;
    const minRtt = rtts.length > 0 ? Math.min(...rtts) : 0;
    const maxRtt = rtts.length > 0 ? Math.max(...rtts) : 0;

    // Calculate standard deviation
    const variance = rtts.length > 0 ? rtts.reduce((sum, rtt) => sum + (rtt - avgRtt) ** 2, 0) / rtts.length : 0;
    const stddev = Math.sqrt(variance);

    return (
      <StatsGrid>
        <StatItem>
          <div className="stat-label">Hop Count</div>
          <div className="stat-value">{trace.hopCount}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Avg RTT</div>
          <div className="stat-value">{this.formatRtt(avgRtt)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Min RTT</div>
          <HighlightedStatValue className="min">{this.formatRtt(minRtt)}</HighlightedStatValue>
        </StatItem>
        <StatItem>
          <div className="stat-label">Max RTT</div>
          <HighlightedStatValue className="max">{this.formatRtt(maxRtt)}</HighlightedStatValue>
        </StatItem>
        <StatItem>
          <div className="stat-label">Std Dev</div>
          <div className="stat-value">{stddev.toFixed(3)}</div>
        </StatItem>
        <StatItem>
          <div className="stat-label">Method</div>
          <div className="stat-value">{trace.method}</div>
        </StatItem>
      </StatsGrid>
    );
  }

  renderTraceHops(hops) {
    if (!hops || hops.length === 0) {
      return null;
    }

    // Find the highest and lowest RTT values
    const validRtts = hops.map((hop) => hop.rtt).filter((rtt) => rtt > 0);
    const maxRtt = validRtts.length > 0 ? Math.max(...validRtts) : 0;
    const minRtt = validRtts.length > 0 ? Math.min(...validRtts) : 0;

    return (
      <Table interactive bordered>
        <thead>
          <tr>
            <th>Hop</th>
            <th>Address</th>
            <th>RTT</th>
            <th>Reply TTL</th>
            <th>ICMP Type/Code</th>
            <th>Reply Size</th>
          </tr>
        </thead>
        <tbody>
          {hops.map((hop) => {
            const isHighestRtt = hop.rtt === maxRtt && hop.rtt > 0;
            const isLowestRtt = hop.rtt === minRtt && hop.rtt > 0 && maxRtt !== minRtt;

            let className = '';
            if (isHighestRtt) {
              className = 'max';
            } else if (isLowestRtt) {
              className = 'min';
            }

            return (
              <HighlightedTableRow key={`hop-${hop.probeTtl}`} className={className}>
                <td>{hop.probeTtl}</td>
                <td>
                  <div>
                    {hop.addr}
                    {hop.name && <HostnameDiv>{hop.name}</HostnameDiv>}
                  </div>
                </td>
                <td>{this.formatRtt(hop.rtt)}</td>
                <td>{hop.replyTtl}</td>
                <td>
                  {hop.icmpType}/{hop.icmpCode}
                </td>
                <td>{hop.replySize}B</td>
              </HighlightedTableRow>
            );
          })}
        </tbody>
      </Table>
    );
  }

  renderTraceResult(traceData, target, agentId, agent_name, testId) {
    if (!traceData || !traceData.trace) {
      return null;
    }

    const { trace } = traceData;

    return (
      <ResultSection key={`${target}-${agentId}-trace`}>
        <div className="section-header">
          <FiServer />
          <span>
            Traceroute Results: {trace.src} → {trace.dst}
          </span>
          <Box ml="auto" fontSize="12px" color="muted">
            Agent: {agent_name || agentId}
          </Box>
        </div>
        <Flex flexDirection="column" gap={2}>
          <Text color="muted" small>
            {trace.method} | {trace.probeCount} probes | {trace.wait}s timeout | {trace.probeSize}B probe size | Stop:{' '}
            {trace.stopReason} | id: {testId}
          </Text>

          <Box>
            <Heading level={6} mb={1}>
              Statistics
            </Heading>
            {this.renderTraceStatistics(trace)}
          </Box>

          {trace.hops && trace.hops.length > 0 && (
            <Box>
              <Heading level={6} mb={1}>
                Hop Details
              </Heading>
              {this.renderTraceHops(trace.hops)}
            </Box>
          )}
        </Flex>
      </ResultSection>
    );
  }

  renderLiveSynthResults(data) {
    if (!data) {
      return (
        <Box p={2}>
          <Text color="muted">No results available</Text>
        </Box>
      );
    }

    // Handle both direct data and results structure
    const results = data.results || [];
    const testId = data.id || '';
    if (!Array.isArray(results) || results.length === 0) {
      return (
        <Box p={2}>
          <Text color="muted">No test results available</Text>
        </Box>
      );
    }

    return (
      <Box>
        {results.map((agentResult, index) => {
          const { result, target, agentId, agent_name } = agentResult;

          if (!result || !result.results) {
            return (
              <Box key={`agent-${agentId || index}-${target || 'unknown'}`} mb={2}>
                <Text color="muted">No results for {target || 'target'}</Text>
              </Box>
            );
          }

          return (
            <Box key={`agent-${agentId}-${target}`} mb={3}>
              {result.results.map((testResult, testIndex) => {
                if (testResult.ping) {
                  return this.renderPingResult(testResult, target, agentId, agent_name, testId);
                }
                if (testResult.trace) {
                  return this.renderTraceResult(testResult, target, agentId, agent_name, testId);
                }
                return (
                  <ResultSection key={`unknown-${agentId}-${target}-${testResult.type || testIndex}`}>
                    <div className="section-header">
                      <span>Unknown Result Type</span>
                    </div>
                    <div className="section-content">
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                        {JSON.stringify(testResult, null, 2)}
                      </pre>
                    </div>
                  </ResultSection>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  }

  renderTestResults(tool) {
    const { raw_result, processed_result } = tool;

    if (!raw_result && !processed_result) {
      return (
        <Box p={2}>
          <Text color="muted">No results available</Text>
        </Box>
      );
    }

    return <Box p={2}>{raw_result && this.renderLiveSynthResults(raw_result)}</Box>;
  }

  render() {
    const { tool, onCloseToolResult, isInlineMode = false } = this.props;

    return (
      <Card
        p={0}
        className="tool-result-livesynth"
        display="flex"
        flexDirection="column"
        gap={0}
        height={isInlineMode ? 'auto' : '100%'}
        maxHeight={isInlineMode ? '600px' : undefined}
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="space-between" p={2} pb={1}>
          <Heading level={isInlineMode ? 6 : 5} m={0} flex={1}>
            {tool.title || 'LiveSynth Tool Result'}
          </Heading>
          <Flex alignItems="center" gap={1}>
            <Button text="Close" small icon="cross" onClick={() => onCloseToolResult(tool)} color="muted" minimal />
          </Flex>
        </Flex>

        <Box flex={1} overflow="auto">
          {this.renderTestResults(tool)}
        </Box>
      </Card>
    );
  }
}

export default LiveSynthToolResult;
